import type { ProjectsLookup } from './project-state.model';
import { isBeforeDate } from '@shared/helpers/date.helpers';

/**
 * Merges a `projects` response over the lookup it is replacing, keeping the
 * projects that appeared while the request was in flight and newer local edits.
 *
 * A project created during the request is not in its response, so replacing the
 * lookup outright loses it — and the router, which resolves the project of a URL
 * against this lookup, then treats the project the user just created as one that
 * does not exist and sends them back to the project list. Projects removed by
 * somebody else still disappear: they were there when the request went out, and
 * the response not having them is what says they are gone.
 */
export const mergeFetchedProjects = (
  fetched: ProjectsLookup,
  current: ProjectsLookup,
  idsWhenFetchStarted: ReadonlySet<string>,
): ProjectsLookup => {
  const preservedProjects = Object.entries(current).filter(([id, project]) => {
    const fetchedProject = fetched[Number(id)];
    return (
      (!idsWhenFetchStarted.has(id) && !(id in fetched)) ||
      (fetchedProject &&
        isBeforeDate(fetchedProject.updated_at, project.updated_at))
    );
  });

  if (!preservedProjects.length) return fetched;

  return { ...fetched, ...Object.fromEntries(preservedProjects) };
};

/** The ids a lookup holds, in the shape {@link mergeFetchedProjects} compares. */
export const projectIds = (lookup: ProjectsLookup): ReadonlySet<string> =>
  new Set(Object.keys(lookup));
